export const en = {
  common: {
    required: "Required",
    all: "All",
    search: "Search",
    details: "Details",
    open: "Open",
    unknown: "Unknown",
    save: "Save",
    saveChanges: "Save changes",
    cancel: "Cancel",
    create: "Create",
    edit: "Edit",
    delete: "Delete",
    remove: "Remove",
    close: "Close",
    refresh: "Refresh",
    copy: "Copy",
    copied: "Copied",
    loading: "Loading…",
    actions: "Actions",
    status: "Status",
    name: "Name",
    created: "Created",
    updated: "Updated",
    none: "None",
    never: "Never",
    active: "Active",
    disabled: "Disabled",
    optional: "Optional",
    loadMore: "Load more",
    retry: "Try again",
    id: "ID",
    set: "Set",
    missing: "Missing",
    yes: "Yes",
    no: "No",
  },
  nav: {
    profiles: "Credential profiles",
    menu: "Menu",
    groupMonitor: "Monitor",
    groupSetup: "Configuration",
    overview: "Overview",
    customers: "Customers",
    logs: "Delivery log",
    account: "Account",
    groupWorkspace: "Workspace",
    groupDelivery: "Delivery",
    groupAdmin: "Administration",
    apps: "Apps",
    credentials: "Credentials",
    providers: "Providers",
    audit: "Audit log",
    settings: "Settings",
    apiDocs: "API reference",
    adminDocs: "Admin API reference",
    signOut: "Sign out",
    language: "Language",
    theme: "Theme",
    light: "Light theme",
    dark: "Dark theme",
    superadmin: "Platform admin",
    groupMessaging: "Messaging",
    send: "Send message",
  },
  auth: {
    title: "Sign in",
    subtitle: "Manage apps, credentials and deliveries for {{app}}.",
    channels: "FCM · APNs · Web Push · Email · SMS · WhatsApp",
    email: "Email",
    emailPlaceholder: "you@company.com",
    password: "Password",
    submit: "Sign in",
  },
  apps: {
    profile: "Credential profile",
    newProfile: "New profile for this app",
    profileHint: "Pick an existing profile to share its credentials, or start with a new, empty one.",
    title: "Apps",
    subtitle: "Each app has its own API keys, credentials and message history.",
    new: "New app",
    createTitle: "Create app",
    name: "App name",
    namePlaceholder: "Aiyo Mobile",
    slug: "Slug",
    slugHint: "Lowercase letters, digits and dashes. Generated from the name when empty.",
    empty: "No apps yet",
    emptyHint: "Create an app to get an API key and start sending.",
    suspend: "Suspend app",
    activate: "Activate app",
    suspendedHint: "A suspended app rejects every API request until you activate it again.",
    dangerZone: "Danger zone",
    deleteHint: "Only apps that never sent a message can be deleted.",
    deleteTitle: "Delete this app?",
    deleteBody: "Its API keys and credential assignments are removed too. This cannot be undone.",
  },
  appDetail: {
    activity: "Last 7 days",
    openOverview: "Overview",
    openLog: "Delivery log",
    openCustomers: "Customers",
    step1: "1. Register the user and their addresses (once, and whenever they change)",
    step2: "2. Send to the user by customer ID",
    overview: "Overview",
    keys: "API keys",
    credentials: "Credential profile",
    messages: "Messages",
    appId: "App ID",
    details: "Details",
    quickStart: "Send your first message",
    quickStartHint: "Create an API key, assign a credential for each channel, then call the API:",
    sendTest: "Send message",
  },
  keys: {
    new: "New API key",
    createTitle: "Create API key",
    name: "Key name",
    namePlaceholder: "backend-production",
    mode: "Mode",
    live: "Live",
    test: "Test",
    modeHint: "Test keys never contact providers. Every message goes to the mock adapter.",
    scopes: "Scopes",
    prefix: "Key",
    lastUsed: "Last used",
    revoked: "Revoked",
    revoke: "Revoke",
    revokeTitle: "Revoke this key?",
    revokeBody: "Requests using this key fail immediately. This cannot be undone.",
    revokedToast: "API key revoked",
    createdTitle: "Copy your API key now",
    createdBody: "This is the only time the full key is shown. Store it in your secret manager.",
    empty: "No API keys yet",
  },
  assignments: {
    subtitle:
      "Credentials this app may use. Clients pick one by alias. Without an alias the lowest priority number per channel is used, and the next one takes over when a provider is down.",
    add: "Add credential",
    editTitle: "Edit credential in profile",
    alias: "Alias",
    aliasHint: "Stable name clients send as `credential`, e.g. email-main.",
    priority: "Priority",
    priorityHint: "1 is tried first.",
    credential: "Credential",
    chooseCredential: "Choose a credential",
    channel: "Channel",
    active: "Active",
    empty: "No credentials in this profile",
    emptyHint: "Add at least one credential for each channel the apps send on.",
    noCredentials: "You have no credentials yet.",
    goToCredentials: "Create one",
    removeTitle: "Remove from this profile?",
    removeBody: "Every app that uses this profile will reject requests that name this alias.",
    swapHint: "Switching the credential keeps the alias, so clients do not need any change.",
  },
  credentials: {
    profilesCount: "Profiles",
    appsCount: "Apps",
    inUseHint: "Remove it from every credential profile before deleting it.",
    title: "Credentials",
    subtitle: "Accounts at each provider. Secrets are encrypted and never shown again after saving.",
    new: "New credential",
    createTitle: "Create credential",
    editTitle: "Edit credential",
    provider: "Provider",
    chooseProvider: "Choose a provider",
    name: "Credential name",
    namePlaceholder: "SES production",
    assignedApps: "Apps",
    health: "Last check",
    test: "Test connection",
    testOk: "Connection OK",
    testFailed: "Connection failed",
    notChecked: "Not checked",
    secretSet: "Saved. Leave empty to keep the current value.",
    empty: "No credentials yet",
    emptyHint: "Add the account details of a provider, then assign it to an app.",
    deleteTitle: "Delete this credential?",
    deleteBody: "Only credentials that are not assigned to any app can be deleted.",
    config: "Configuration",
    saved: "Credential saved",
    disableHint: "Disabled credentials are skipped and the next priority takes over.",
  },
  providers: {
    title: "Providers",
    subtitle:
      "Provider status applies to every organization. Disable a provider during an outage and traffic moves to the next credential by priority.",
    code: "Code",
    channel: "Channel",
    note: "Note",
    notePlaceholder: "Incident or reason",
    setStatus: "Change status",
    superadminOnly: "Only platform admins can change provider status.",
    mock: "Mock",
    statusHelp: {
      active: "Used normally.",
      degraded: "Used only when no healthy credential is left.",
      disabled: "Never used. Credentials of this provider are skipped.",
    },
    updated: "Provider updated",
  },
  messages: {
    message: "Message",
    empty: "No messages yet",
    emptyHint: "Send one with the API or the Send message button.",
    sendTitle: "Send message",
    sendHint: "The body is the same as POST /v1/messages.",
    body: "Request body (JSON)",
    testMode: "Test mode: use the mock adapter, no provider is contacted",
    channels: "Channels",
    channel: "Channel",
    progress: "Progress",
    priority: "Priority",
    category: "Category",
    mode: "Mode",
    createdAt: "Created",
    completedAt: "Completed",
    perChannel: "Per channel",
    deliveries: "Deliveries",
    address: "Address",
    attempts: "Attempts",
    provider: "Provider",
    alias: "Alias",
    error: "Error",
    events: "Events",
    rendered: "Payload sent",
    metadata: "Metadata",
    providerMessageId: "Provider message ID",
    autoRefresh: "Updating automatically while the message is processing.",
    deliveryTitle: "Delivery",
    invalidJson: "The body is not valid JSON.",
    duplicate: "Duplicate",
    audience: "Recipients",
    audienceAll: "All customers (broadcast)",
    audienceCustomers: "Customers: {{count}}",
    audienceAddresses: "Direct addresses: {{count}}",
    content: "Content",
    contentHint: "What recipients see on each channel.",
    counts: {
      total: "Total",
      pending: "Pending",
      sent: "Sent",
      delivered: "Delivered",
      failed: "Failed",
      skipped: "Skipped",
    },
  },
  settings: {
    tabs: {
      general: "General",
      access: "Dashboard access",
      delivery: "Delivery",
      environment: "Server",
    },
    discard: "Discard",
    unsavedTab: "Unsaved changes",
    title: "Settings",
    subtitle:
      "Platform settings apply to every app and reach all servers within 10 seconds, without a restart.",
    superadminOnly: "Only platform admins can view and change settings.",
    saved: "Settings saved",
    unsaved: "You have unsaved changes.",
    lastUpdated: "Last updated {{when}} by {{who}}",
    neverUpdated: "Using defaults. Nothing has been changed yet.",
    general: "General",
    publicUrl: "Public URL",
    publicUrlHint: "The address clients use to reach the API. Shown in the API reference.",
    logLevel: "Log level",
    logLevelHint: "Applies to the API and every worker.",
    mapStyleLight: "Map style, light theme",
    mapStyleDark: "Map style, dark theme",
    mapStyleHint: "A MapLibre style URL for the customer map. Leave empty to use OpenFreeMap.",
    access: "Dashboard access",
    trustedOrigins: "Allowed dashboard origins",
    trustedOriginsHint:
      "One origin per line, e.g. https://dashboard.example.com. These may call the admin API with a session.",
    envOrigins: "Always allowed (set in the server environment)",
    sessionTtl: "Session lifetime (hours)",
    sessionTtlHint: "Applies to new sign-ins.",
    delivery: "Delivery",
    providerTimeout: "Provider timeout (ms)",
    providerTimeoutHint: "How long one call to a provider may take before it is retried.",
    lanes: "Priority lanes",
    lanesHint:
      "Each priority has its own queue, so a large broadcast never delays an OTP. Pause a lane to hold its messages, for example while a provider is down.",
    laneRunning: "Running",
    lanePaused: "Paused",
    laneConcurrency: "Parallel sends",
    pausedWarning:
      "Messages in a paused lane are accepted and stay queued. They are sent once the lane runs again.",
    concurrencyHint:
      "Parallel sends is per worker. Changes apply to running workers within a few seconds, without a restart.",
    environment: "Server environment",
    environmentHint:
      "These values are read from the server environment because they are needed before the database is available or are unsafe to change from here. Change them in the deployment and restart.",
    env: {
      nodeEnv: "Environment",
      port: "API port",
      poolMax: "Database connections",
      databaseUrl: "Database URL",
      masterKey: "Credential encryption key",
      keyVersion: "Encryption key version",
      cookieSecure: "Secure session cookie",
      cookieSameSite: "Cookie SameSite",
      origins: "Dashboard origins",
    },
  },
  overview: {
    title: "Overview",
    subtitle: "How notifications are doing across your apps.",
    allApps: "All apps",
    app: "App",
    period: "Period",
    periods: { "1": "24 hours", "7": "7 days", "30": "30 days", "90": "90 days" },
    includeTest: "Include test messages",
    kpi: {
      deliveries: "Deliveries",
      reached: "Reached",
      problems: "Not received",
      messages: "Messages",
      customers: "Customers",
    },
    kpiHint: {
      reached: "Delivered or accepted by the provider",
      problems: "Failed, skipped or bounced",
      customers: "{{count}} new in this period",
    },
    vsPrevious: "vs previous period",
    noPrevious: "No data in the previous period",
    map: {
      title: "Where your customers are",
      description: "Customers and undelivered notifications by area. Deliveries follow the selected period.",
      customers: "Customers",
      metric: "Show",
      metricCustomers: "Customers",
      metricProblems: "Not received",
      areas: "Top areas",
      area: "Area",
      coverage:
        "{{exact}} with coordinates, {{approximate}} placed at their city centre, {{unlocated}} without a location.",
      precision: { city: "Approximate: city centre", region: "Approximate: province capital" },
      empty: "No customer has a location yet. Send a city or coordinates when registering customers.",
      noWebgl: "This browser cannot draw the map because WebGL is turned off.",
      gestureWindows: "Use Ctrl + scroll to zoom the map",
      gestureMac: "Use ⌘ + scroll to zoom the map",
      gestureMobile: "Use two fingers to move the map",
      zoomIn: "Zoom in",
      zoomOut: "Zoom out",
      viewCustomers: "View customers in {{area}}",
    },
    heatmap: {
      title: "When notifications go out",
      description: "Deliveries by weekday and hour, in {{tz}}.",
      volume: "Volume",
      problems: "Not received",
      metric: "Show",
      less: "Less",
      more: "More",
      cell: "{{day}}, {{hour}}: {{total}} deliveries, {{problems}} not received",
      busiest: "Busiest: {{when}}",
      empty: "No deliveries in this period.",
    },
    daily: {
      title: "Deliveries per day",
      reached: "Reached",
      problems: "Not received",
      tooltip: "{{date}}: {{total}} deliveries, {{problems}} not received",
    },
    channels: {
      title: "By channel",
      deliveries: "{{count}} deliveries",
      rate: "{{rate}} reached",
    },
    issues: {
      title: "Why notifications were not received",
      description: "Grouped by cause. Open one to see the affected deliveries.",
      none: "Every notification in this period reached its provider.",
      openLog: "Open delivery log",
    },
    empty: "No deliveries in this period",
    emptyHint: "Numbers appear here once your apps send messages.",
  },
  customers: {
    cityFilter: "Area: {{city}}",
    clearCity: "Remove area filter",
    title: "Customers",
    subtitle: "Users your apps registered with the gateway, and where each one can receive notifications.",
    searchPlaceholder: "Customer ID, name, email or phone number",
    search: "Search customers",
    columns: {
      customer: "Customer",
      app: "App",
      channels: "Reachable on",
      location: "Location",
      lastActive: "Last active",
    },
    empty: "No customers yet",
    emptyHint: "Customers appear here once your app's backend registers them through the API.",
    apiReference: "API reference",
    noResults: "No customers match your search.",
    invalidCount: "{{count}} not usable",
    subscriptionStatus: { active: "Active", unsubscribed: "Unsubscribed", invalid: "Invalid" },
    locationSource: { backend: "From your backend", ip: "From IP address", gps: "From GPS" },
    detail: {
      sendTest: "Send test notification",
      profile: "Profile",
      externalId: "Customer ID",
      attributes: "Attributes",
      tags: "Tags",
      language: "Language",
      timezone: "Time zone",
      location: "Location",
      registered: "Registered",
      lastActive: "Last active",
      reach: "Addresses",
      reachHint:
        "Push goes to every active device. Email, SMS and WhatsApp go to the primary address of each channel.",
      noSubscriptions: "This customer has no addresses yet, so no notification can reach them.",
      primary: "Primary",
      device: "Device",
      lastSeen: "Last seen {{when}}",
      unsubscribe: "Unsubscribe",
      reactivate: "Reactivate",
      subscriptionUpdated: "Address updated",
      activity: "Last {{days}} days",
      lastDelivery: "Last notification {{when}}",
      history: "Notification history",
      historyHint:
        "Every delivery to this customer, newest first. Open one to see what happened and what to do.",
      historyEmpty: "No notifications were sent to this customer yet.",
      dangerZone: "Danger zone",
      erase: "Erase customer",
      eraseHint: "For data deletion requests under UU PDP. The profile and addresses are deleted.",
      eraseTitle: "Erase this customer?",
      eraseBody:
        "The profile and every address are deleted. Past deliveries stay in the statistics without the address and content. This cannot be undone.",
      erased: "Customer erased",
    },
  },
  logs: {
    title: "Delivery log",
    subtitle: "Find out why someone did or did not get a notification.",
    searchPlaceholder: "Customer ID, email, phone number, push token or message ID",
    search: "Search deliveries",
    filters: {
      app: "App",
      allApps: "All apps",
      channel: "Channel",
      allChannels: "All channels",
      outcome: "Result",
      allOutcomes: "All results",
      period: "Period",
      anyTime: "Any time",
      reason: "Reason",
      anyReason: "Any reason",
      toggle: "Filters",
      clear: "Clear filters",
    },
    outcomes: { problems: "Not received", delivered: "Reached", in_progress: "In progress" },
    periods: { "1": "Last 24 hours", "7": "Last 7 days", "30": "Last 30 days" },
    columns: {
      time: "Time",
      recipient: "Recipient",
      message: "Message",
      channel: "Channel",
      result: "Result",
    },
    directAddress: "Direct address",
    noAddress: "No address",
    empty: "No deliveries match these filters",
    emptyHint: "Try a longer period or clear the filters.",
    nothingYet: "No deliveries yet",
    nothingYetHint: "Deliveries appear here as soon as an app sends a message.",
    test: "Test",
  },
  issues: {
    delivered: {
      label: "Delivered",
      explain: "The provider confirmed the message reached the device or inbox.",
      action:
        "Nothing to fix here. If the user did not see it, check the notification settings on their device or their spam folder.",
    },
    accepted: {
      label: "Sent to provider",
      explain:
        "The provider accepted the message. Push providers do not report whether the device actually showed it.",
      action:
        "If the user did not see it, check that notifications are allowed for the app, that the app is not restricted by battery saving, and that they are signed in on that device.",
    },
    in_progress: {
      label: "In progress",
      explain: "The message is queued or being sent right now.",
      action: "Wait a moment. This page updates on its own.",
    },
    retrying: {
      label: "Retrying",
      explain:
        "The last attempt failed with a temporary error. The gateway tries again automatically, up to 5 times.",
      action: "Nothing to do yet. If many deliveries keep retrying, check the provider status.",
    },
    customer_not_found: {
      label: "Customer not registered",
      explain: "The app sent to a customer ID that the gateway does not know.",
      action:
        "The app's backend must register the user with exactly the same customer ID before sending. Check for typos or a different ID format.",
    },
    no_subscription: {
      label: "No address for this channel",
      explain:
        "The customer is registered, but has no address for this channel, so there was nowhere to send.",
      action:
        "For push, ask the user to open the app and allow notifications. For email, SMS or WhatsApp, the backend must add the address.",
    },
    subscription_invalid: {
      label: "Address no longer valid",
      explain:
        "An earlier attempt showed this address stopped working, for example the app was uninstalled or the push token expired. The gateway no longer uses it.",
      action:
        "For push, the user must open the app again so it registers a new token. For email, confirm the address with the user, then reactivate it on the customer page.",
    },
    unsubscribed: {
      label: "Unsubscribed",
      explain: "The customer opted out of this channel.",
      action: "Respect the opt-out. The user can opt in again from the app.",
    },
    address_invalid: {
      label: "Address rejected",
      explain:
        "The provider said this address does not exist or is no longer registered. The gateway marked it invalid so it is not used again.",
      action:
        "For push, the app was probably uninstalled or the token expired; a new one is registered when the user opens the app. For email, check the address for typos.",
    },
    token_wrong_project: {
      label: "Token from another app",
      explain: "The push token belongs to a different Firebase project than the credential used to send.",
      action:
        "Check that the app uses the right Firebase project, or assign the matching FCM credential under the alias the app sends.",
    },
    credential_problem: {
      label: "Credential problem",
      explain: "The provider rejected the credential, or no usable credential is assigned for this channel.",
      action:
        "An admin should run Test connection on the Credentials page and check the app's assignments. This affects everyone, not only this user.",
    },
    provider_unavailable: {
      label: "Provider unavailable",
      explain: "The provider did not respond or reported a temporary error, and every retry failed.",
      action:
        "Check the provider's status page. Send again once it recovers, or assign a backup credential so traffic fails over.",
    },
    payload_rejected: {
      label: "Content rejected",
      explain:
        "The provider rejected the message content, for example it was too large or had an invalid field.",
      action: "The sending app must fix the content. The technical details show the provider's reason.",
    },
    unknown_outcome: {
      label: "Outcome unknown",
      explain:
        "The worker stopped while sending, so it is not known whether the message went out. It was not resent automatically to avoid a duplicate and a double charge.",
      action: "Send it again manually if it matters.",
    },
    bounced: {
      label: "Bounced",
      explain: "The message was accepted, then returned by the recipient's server.",
      action: "Check the address with the user.",
    },
    other: {
      label: "Failed",
      explain: "The provider returned an error that has no plain explanation here yet.",
      action: "The technical details below show the provider's own message.",
    },
  },
  delivery: {
    title: "Delivery details",
    whatHappened: "What happened",
    whatToDo: "What to do",
    customer: "Customer",
    address: "Address",
    app: "App",
    channel: "Channel",
    message: "Message",
    content: "Content",
    timeline: "Timeline",
    technical: "Technical details",
    provider: "Provider",
    credential: "Credential alias",
    attempts: "Attempts",
    providerMessageId: "Provider message ID",
    errorCode: "Error code",
    errorMessage: "Provider response",
    payload: "Payload sent",
    events: "Raw events",
    subscription: "Address status",
    deliveryId: "Delivery ID",
    openMessage: "Open message",
    openCustomer: "Open customer",
    steps: {
      created: "Accepted by the gateway",
      sent: "Handed to the provider",
      delivered: "Delivered",
      failed: "Failed",
      retry_scheduled: "Attempt failed, will retry",
      skipped: "Not sent",
      bounced: "Bounced",
      opened: "Opened",
      clicked: "Clicked",
      unsubscribed: "Unsubscribed",
      retry_requested: "Retry requested",
      retry_new_device: "Added on retry for a newer device",
    },
    retryIn: "Next attempt in {{seconds}} seconds",
    retryBy: "by {{who}}",
    retryAddress: "now to {{address}}",
    retryCredential: "with credential {{alias}}",
  },
  account: {
    title: "Account",
    subtitle: "Your profile and how you sign in.",
    profile: "Profile",
    name: "Display name",
    email: "Email",
    role: "Role",
    organization: "Organization",
    profileSaved: "Profile saved",
    roles: { owner: "Owner", admin: "Admin", viewer: "Viewer" },
    password: "Password",
    passwordHint: "After you change it, every other device where you are signed in is signed out.",
    currentPassword: "Current password",
    newPassword: "New password",
    confirmPassword: "Confirm new password",
    minLength: "At least {{count}} characters.",
    tooShort: "Use at least {{count}} characters.",
    mismatch: "The passwords do not match.",
    changePassword: "Change password",
    passwordChanged: "Password changed. Your other sessions were signed out.",
    show: "Show password",
    hide: "Hide password",
  },
  composer: {
    compose: "Compose",
    json: "JSON",
    emails: "Email addresses",
    tokens: "FCM devices",
    tokensHint: "One per line. Firebase installation IDs (FID) and older registration tokens both work.",
    title: "Title",
    message: "Message",
    sendTest: "Send test",
    priorityHint: {
      high: "OTP and urgent messages",
      normal: "Most transactional messages",
      low: "Broadcasts and promotions",
    },
  },
  send: {
    title: "Send message",
    subtitle:
      "Send to every customer of an app, to chosen customers, or to addresses directly. The preview shows what recipients will see.",
    viewer: "Viewers cannot send messages. Ask an admin of your organization.",
    noApps: "No active app",
    noAppsHint: "Create an app, or activate a suspended one, to send from the dashboard.",
    app: "App",
    testMode: "Test mode",
    testModeHint: "Uses the mock adapter. Nothing reaches a real provider or a real person.",
    audience: {
      title: "Recipients",
      all: "All customers",
      customers: "Customers",
      addresses: "Addresses",
      addressesHint: "Send without registered customers. Fields appear for the channels you pick below.",
      phones: "Phone numbers",
      phonesHint: "E.164 format, one per line. Used for SMS and WhatsApp.",
    },
    customers: {
      search: "Find customers",
      searchPlaceholder: "Customer ID, name, email or phone number",
      results: "Matching customers",
      noMatch: "No customer of this app matches.",
      selectedLabel: "Selected customers",
      selected: "Selected: {{count}}",
      clear: "Clear",
      paste: "Paste IDs",
      pasteLabel: "Customer IDs",
      pasteHint: "Separate IDs with commas, spaces or new lines.",
      addPasted: "Add ({{count}})",
    },
    reach: {
      pickChannel: "Pick a channel below to see who this reaches.",
      summary: "Reaches {{reachable}} of {{total}} customers.",
      nobody: "No customer of this app has an address on these channels yet.",
      devices: "{{customers}} customers on {{devices}} devices",
      customers: "{{customers}} customers",
      hint: "Customers without an address on a channel are left out on that channel. Opted-out and invalid addresses are never used.",
    },
    channelsHint: "Each channel needs a credential in the app's credential profile.",
    channel: { fcm: "Push (FCM)", email: "Email", sms: "SMS", whatsapp: "WhatsApp" },
    noCredential: "No credential for this channel in the app's profile.",
    credential: "Credential",
    credentialHint: "Which credential of the app's profile sends this channel.",
    credentialDefault: "Default (by priority)",
    pushCredentialHint: "Devices registered with their own credential keep it.",
    fcm: {
      image: "Image URL",
      link: "Link (web)",
      linkHint: "HTTPS page opened when a web notification is clicked.",
      data: "Data",
      dataHint: "key=value, one per line. Passed to the app.",
    },
    email: {
      subject: "Subject",
      fromName: "Sender name",
      fromNameHint: "Leave empty to use the credential's sender name.",
      replyTo: "Reply-to",
      format: "Format",
      plain: "Plain text",
      html: "HTML",
      htmlHint: "Inline styles work best: many mail clients ignore <style> blocks and never run scripts.",
      textAlt: "Plain-text version",
      textAltHint: "Optional. Shown by mail clients that do not display HTML.",
      text: "Message",
    },
    sms: { text: "Message" },
    whatsapp: {
      template: "Template name",
      templateHint: "A template approved by Meta.",
      language: "Language",
      variables: "Variables",
      variablesHint: "One per line, in template order.",
    },
    delivery: "Delivery",
    categoryHint: "For example transactional or marketing. Broadcasts must have one.",
    review: "Review and send",
    preview: "Preview",
    previewHint: "Approximate: each device and mail client renders a little differently.",
    previewEmpty: "Pick a channel to see a preview",
    errors: {
      channel: "Choose at least one channel.",
      customers: "Choose at least one customer.",
      addresses: "Add at least one address for the chosen channels.",
      category: "A broadcast needs a category.",
      data: "Every line must look like key=value.",
      pushContent: "Add a title, a message or data.",
      https: "Use an HTTPS link.",
      subject: "Add a subject.",
      emailBody: "Add the email content.",
      smsText: "Add the message.",
      template: "Add the template name.",
      fix: "Fix the highlighted fields first.",
    },
    confirm: {
      title: "Send this message?",
      test: "Test mode: the mock adapter handles it and no one is contacted.",
      live: "This goes out to real people right away.",
      send: "Send now",
      recipients: "Recipients",
      all: "All customers with an address on these channels (about {{n}})",
      allUnknown: "All customers with an address on these channels",
      customers: "Customers: {{count}}",
      addresses: "Direct addresses: {{count}}",
      broadcastWarning: "This is a broadcast. It cannot be stopped once it has started sending.",
    },
  },
  preview: {
    channel: "Channel",
    yourApp: "Your app",
    now: "now",
    dataOnly: "Data-only message: the app receives it without showing a notification.",
    pushEmpty: "Add a title or a message to see the notification.",
    dataKeys: "Data:",
    email: {
      from: "From",
      fromCredential: "(address from the credential)",
      to: "To",
      toRecipient: "Each recipient",
      replyTo: "Reply-to",
      subject: "Subject",
      view: "View",
      views: { html: "HTML", text: "Plain text", source: "Source" },
      width: "Width",
      desktop: "Desktop",
      mobile: "Mobile",
      frameTitle: "Email preview",
      scaled: "Laid out at {{width}} px, shown at {{percent}}%",
    },
    sms: { count: "{{chars}} characters · {{parts}} SMS part(s)", unicode: "Unicode, shorter parts" },
    whatsapp: {
      template: "Template",
      hint: "WhatsApp shows the approved template text with these variables filled in, in order.",
    },
  },
  retry: {
    open: "Retry",
    one: "Retry",
    title: "Retry deliveries",
    description:
      "Sends again with the credentials and customer addresses as they are now, so fix the cause first. Nothing is sent until you confirm.",
    submit: "Retry {{count}}",
    submitPlain: "Retry",
    nothing: "There is nothing to retry here: no failed or skipped delivery matches.",
    summary: "{{count}} of {{total}} failed or skipped deliveries can be sent again.",
    actions: {
      resend: {
        title: "Send again",
        hint: "Same content. Customers get it at their current address, and the credential is picked as configured now.",
      },
      recheck: {
        title: "Check the address again",
        hint: "There was no usable address. These go out only to customers who have one now, for push including devices registered since.",
      },
      confirm: {
        title: "Outcome unknown",
        hint: "The worker stopped while sending, so these may already have arrived.",
      },
      never: {
        title: "Not retried",
        hint: "A retry cannot help: the customer opted out, the content was rejected, or a direct address does not exist.",
      },
    },
    includeUnknown: "Also send deliveries with an unknown outcome",
    unknownWarning: "They may arrive twice, and SMS or WhatsApp may be charged twice.",
    credentials: {
      title: "Credentials used now",
      hint: "Test the connection before you retry. If the credential was the problem, choose another one here.",
      hintManyApps:
        "These deliveries belong to several apps. To choose another credential, filter by one app.",
      deliveries: "Deliveries: {{count}}",
      willUse: "Will use",
      edited: "edited {{when}}",
      error: "No usable credential right now, so these would fail again.",
      unchanged:
        "This is the same credential, unchanged since these deliveries failed on it. Fix it on the Credentials page or choose another before retrying.",
      use: "Send with",
      keep: "As each delivery asked",
      unusable: "Disabled or provider unavailable",
    },
    progress: "Retry progress",
    progressText: "{{done}} of {{total}} handled",
    done: "Queued again: {{count}}",
    doneUnchanged: "Left as they were, still without a usable address: {{count}}",
  },
  profiles: {
    title: "Credential profiles",
    subtitle:
      "A profile is a named set of credentials under aliases. Each app sends with one profile, so moving an app to Mock or Backup is a single change.",
    new: "New profile",
    createTitle: "Create credential profile",
    editTitle: "Edit profile",
    name: "Profile name",
    namePlaceholder: "Production",
    description: "Description",
    descriptionPlaceholder: "What this profile is for",
    copyFrom: "Start from",
    copyFromNone: "Empty profile",
    copyFromHint: "Copy the credentials of another profile, then swap what you need.",
    created: "Profile created",
    saved: "Profile saved",
    deleted: "Profile deleted",
    usedBy: "Used by",
    noApps: "No app uses this profile yet.",
    credentials: "Credentials",
    channels: "Channels",
    empty: "No credential profiles yet",
    emptyHint: "Every app gets a profile when it is created. Add one here to prepare a Mock or Backup set.",
    entries: "Credentials in this profile",
    entriesHint:
      "Clients pick a credential by alias. Without an alias, the lowest priority number per channel is used, and the next one takes over when a provider is down.",
    shared: "Changes here apply right away to every app that uses this profile: {{apps}}.",
    dangerZone: "Danger zone",
    deleteHint: "Only a profile that no app uses can be deleted. Its credentials are kept.",
    deleteTitle: "Delete this profile?",
    deleteBody: "The aliases in this profile are removed. The credentials themselves are kept.",
    inUse: "Move the apps that use this profile to another profile first.",
    app: {
      current: "This app sends with",
      change: "Change profile",
      edit: "Edit profile",
      changeTitle: "Change credential profile",
      changeHint: "Every alias switches at once. The change reaches the workers within a few seconds.",
      target: "New profile",
      switched: "Credential profile changed",
      missingAliases: "Not in the new profile, so requests that name them will be rejected: {{aliases}}",
      missingChannels: "No credential for these channels in the new profile: {{channels}}",
      pushChanged:
        "Different push credentials for {{aliases}}. A device token belongs to one Firebase project, so tokens registered with the current credential may stop working.",
      same: "The new profile covers the same aliases and channels.",
      readOnly: "To change these credentials, edit the profile. Every app that uses it changes too.",
    },
  },
  status: {
    message: {
      scheduled: "Scheduled",
      queued: "Queued",
      processing: "Processing",
      completed: "Completed",
      partially_failed: "Partially failed",
      failed: "Failed",
      cancelled: "Cancelled",
    },
    delivery: {
      pending: "Pending",
      sending: "Sending",
      sent: "Sent",
      delivered: "Delivered",
      failed: "Failed",
      skipped: "Skipped",
      bounced: "Bounced",
    },
    provider: { active: "Active", degraded: "Degraded", disabled: "Disabled" },
    credential: { active: "Active", disabled: "Disabled" },
    app: { active: "Active", suspended: "Suspended" },
  },
  channels: {
    fcm: "FCM",
    apns: "APNs",
    webpush: "Web Push",
    email: "Email",
    sms: "SMS",
    whatsapp: "WhatsApp",
  },
  priority: { high: "High", normal: "Normal", low: "Low" },
  audit: {
    filter: "Type",
    allTypes: "All changes",
    detail: "Change details",
    field: "Field",
    before: "Before",
    after: "After",
    noDiff: "No field changes were recorded for this action.",
    ip: "IP address",
    actions: {
      create: "Created",
      update: "Updated",
      delete: "Deleted",
      revoke: "Revoked",
      erase: "Erased",
      change_password: "Changed password",
    },
    entities: {
      credential_profile: "Credential profile",
      profile_credential: "Profile entry",
      app: "App",
      api_key: "API key",
      credential: "Credential",
      app_credential: "Credential assignment",
      provider: "Provider",
      settings: "Platform settings",
      customer: "Customer",
      subscription: "Customer address",
      user: "User",
    },
    title: "Audit log",
    subtitle: "Every configuration change, newest first.",
    actor: "Who",
    action: "Action",
    entity: "What",
    when: "When",
    changes: "Changes",
    empty: "No changes recorded yet",
  },
  errors: {
    generic: "Something went wrong",
    network: "Cannot reach the API. Check that it is running and that VITE_API_BASE_URL points to it.",
    notFound: "Page not found",
    notFoundHint: "The page you opened does not exist.",
    codes: {
      profile_in_use: "This profile is still used by an app. Move the app to another profile first.",
      profile_name_taken: "A profile with this name already exists.",
      wrong_password: "The current password is incorrect.",
      same_password: "Choose a password that is different from the current one.",
      invalid_credentials: "Email or password is incorrect.",
      rate_limited: "Too many attempts. Try again in a few minutes.",
      forbidden: "You do not have permission to do this.",
      unauthorized: "Your session has ended. Sign in again.",
      slug_taken: "An app with this slug already exists.",
      alias_taken: "This alias is already used in this app.",
      credential_in_use: "This credential is still assigned to an app.",
      app_has_messages: "This app has sent messages. Suspend it instead.",
      credential_channel_mismatch: "That credential belongs to another channel.",
      validation_error: "Some fields are invalid.",
    },
  },
};

type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };
export type Translation = Shape<typeof en>;
